import { useState, useMemo } from 'react';
import { Brain, AlertTriangle, TrendingUp, Users, BookOpen, ChevronDown } from 'lucide-react';
import { colors, Card, EmptyState, ConfidenceBadge } from '../../shared';
import { useData } from '../../../contexts/DataContext';
import { KNOWLEDGE_CATEGORIES } from '../../../utils/knowledgeEngine';
import { isActiveWO } from '../../../utils/dataQueries';

// ─── Stat Card ───────────────────────────────────────────────────────────────

const StatCard = ({ icon: Icon, label, value, subValue, color = colors.yellow }) => (
  <Card>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm mb-1" style={{ color: colors.textSecondary }}>{label}</p>
        <p className="text-2xl font-bold" style={{ color: colors.textPrimary }}>{value}</p>
        {subValue && <p className="text-xs mt-1" style={{ color: colors.textMuted }}>{subValue}</p>}
      </div>
      <div className="p-2 rounded-lg" style={{ background: `${color}20` }}>
        <Icon size={20} style={{ color }} />
      </div>
    </div>
  </Card>
);

// ─── Property Health Card ────────────────────────────────────────────────────

const HealthCard = ({ property, healthData, selected, onClick }) => {
  const score = healthData?.score ?? 100;
  const scoreColor = score >= 75 ? colors.green : score >= 50 ? colors.yellow : colors.red;

  return (
    <Card
      onClick={onClick}
      style={{
        border: selected ? `2px solid ${colors.yellow}` : `1px solid ${colors.border}`,
        cursor: 'pointer',
      }}
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-sm font-medium truncate" style={{ color: colors.textPrimary }}>
            {property.name || property.address || property.id}
          </p>
          <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>
            {healthData?.details?.totalUnits || 0} units &middot; {healthData?.details?.openWOs || 0} open WOs
          </p>
        </div>
        <div className="text-right shrink-0 ml-3">
          <p className="text-xl font-bold" style={{ color: scoreColor }}>{score}</p>
          <p className="text-[10px]" style={{ color: colors.textMuted }}>/100</p>
        </div>
      </div>
    </Card>
  );
};

// ─── Seasonal Chart (inline bar) ─────────────────────────────────────────────

const SeasonalChart = ({ data }) => {
  if (!data || data.length === 0) return <EmptyState title="No seasonal data" />;

  const maxCount = Math.max(...data.map(d => d.count), 1);

  return (
    <div className="flex items-end gap-1" style={{ height: 120 }}>
      {data.map(d => (
        <div key={d.month} className="flex-1 flex flex-col items-center justify-end h-full">
          <div
            className="w-full rounded-t"
            style={{
              height: `${Math.max((d.count / maxCount) * 100, 4)}%`,
              background: d.isSpike ? colors.yellow : colors.textMuted,
              minHeight: 4,
            }}
          />
          <p className="text-[10px] mt-1" style={{ color: d.isSpike ? colors.yellow : colors.textMuted }}>
            {d.monthName}
          </p>
        </div>
      ))}
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

export default function IntelligenceTab({ permissions }) {
  const {
    properties, technicians, units, workOrders,
    getPropertyHealth, getRecurringIssues, getCrossPropertyPatterns,
    getSeasonalPatterns, getTechReliability, getKnowledgeStats,
  } = useData();

  const [selectedPropertyId, setSelectedPropertyId] = useState(null);

  // ── Property Health Grid ────────────────────────────────────────────────────

  const propertyHealthList = useMemo(() => {
    return properties
      .map(p => ({
        property: p,
        health: getPropertyHealth(p.id),
      }))
      .sort((a, b) => (a.health?.score ?? 100) - (b.health?.score ?? 100));
  }, [properties, getPropertyHealth]);

  const selectedProperty = properties.find(p => p.id === selectedPropertyId);
  const selectedHealth = selectedPropertyId ? getPropertyHealth(selectedPropertyId) : null;

  // ── Recurring Issues for selected property ──────────────────────────────────

  const recurringIssues = useMemo(() => {
    if (!selectedPropertyId) return [];
    const propUnits = units.filter(u => u.propertyId === selectedPropertyId);
    const allRecurring = [];
    for (const u of propUnits) {
      const issues = getRecurringIssues(u.unitId || u.id);
      for (const issue of issues) {
        allRecurring.push({ ...issue, unitId: u.unitId || u.id, unit: u.unit || u.unitId || u.id });
      }
    }
    return allRecurring.sort((a, b) => b.occurrences - a.occurrences);
  }, [selectedPropertyId, units, getRecurringIssues]);

  // ── Cross-Property Patterns ─────────────────────────────────────────────────

  const crossPatterns = useMemo(() => {
    if (!selectedPropertyId) return [];
    return getCrossPropertyPatterns(selectedPropertyId);
  }, [selectedPropertyId, getCrossPropertyPatterns]);

  // ── Seasonal Patterns ───────────────────────────────────────────────────────

  const seasonalData = useMemo(() => {
    return getSeasonalPatterns(selectedPropertyId);
  }, [selectedPropertyId, getSeasonalPatterns]);

  // ── Tech Reliability ────────────────────────────────────────────────────────

  const techReliability = useMemo(() => {
    return technicians
      .map(t => ({
        name: t.name,
        ...getTechReliability(t.name),
      }))
      .filter(t => t.totalWOs > 0)
      .sort((a, b) => b.score - a.score);
  }, [technicians, getTechReliability]);

  // ── Knowledge Coverage ──────────────────────────────────────────────────────

  const knowledgeStats = useMemo(() => {
    if (!selectedPropertyId) return null;
    return getKnowledgeStats(selectedPropertyId);
  }, [selectedPropertyId, getKnowledgeStats]);

  // ── Overview stats ──────────────────────────────────────────────────────────

  const overviewStats = useMemo(() => {
    const avgScore = propertyHealthList.length > 0
      ? Math.round(propertyHealthList.reduce((s, p) => s + (p.health?.score ?? 100), 0) / propertyHealthList.length)
      : 100;
    const atRisk = propertyHealthList.filter(p => (p.health?.score ?? 100) < 50).length;
    const openWOs = workOrders.filter(wo => isActiveWO(wo)).length;
    return { avgScore, atRisk, openWOs };
  }, [propertyHealthList, workOrders]);

  return (
    <div className="p-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-lg" style={{ background: `${colors.yellow}20` }}>
          <Brain size={24} style={{ color: colors.yellow }} />
        </div>
        <div>
          <h2 className="text-xl font-bold" style={{ color: colors.textPrimary }}>Maintenance Intelligence</h2>
          <p className="text-sm" style={{ color: colors.textSecondary }}>Property health, patterns, and tech reliability</p>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatCard icon={TrendingUp} label="Avg Health Score" value={overviewStats.avgScore} subValue={`${properties.length} properties`} color={colors.green} />
        <StatCard icon={AlertTriangle} label="At-Risk Properties" value={overviewStats.atRisk} subValue="Score below 50" color={colors.red} />
        <StatCard icon={Brain} label="Open Work Orders" value={overviewStats.openWOs} color={colors.yellow} />
      </div>

      {/* Property Health Grid */}
      <Card className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold" style={{ color: colors.textSecondary }}>Property Health</h3>
          {selectedPropertyId && (
            <button
              onClick={() => setSelectedPropertyId(null)}
              className="text-xs px-2 py-1 rounded"
              style={{ color: colors.yellow }}
            >
              Clear selection
            </button>
          )}
        </div>
        {propertyHealthList.length === 0 ? (
          <EmptyState title="No properties found" />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {propertyHealthList.map(({ property, health }) => (
              <HealthCard
                key={property.id}
                property={property}
                healthData={health}
                selected={property.id === selectedPropertyId}
                onClick={() => setSelectedPropertyId(property.id === selectedPropertyId ? null : property.id)}
              />
            ))}
          </div>
        )}
      </Card>

      {/* Detail sections — show when a property is selected */}
      {selectedPropertyId && (
        <>
          {/* Recurring Issues */}
          <Card className="mb-6">
            <h3 className="text-sm font-semibold mb-4" style={{ color: colors.textSecondary }}>
              Recurring Issues — {selectedProperty?.name || selectedPropertyId}
            </h3>
            {recurringIssues.length === 0 ? (
              <p className="text-xs" style={{ color: colors.textMuted }}>No recurring issues detected.</p>
            ) : (
              <div className="space-y-2">
                {recurringIssues.slice(0, 10).map((issue, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-lg" style={{ background: colors.inputBg }}>
                    <div className="flex-1">
                      <p className="text-sm" style={{ color: colors.textPrimary }}>{issue.description}</p>
                      <p className="text-xs mt-1" style={{ color: colors.textMuted }}>
                        Unit {issue.unit} &middot; {issue.occurrences}x &middot; {issue.firstSeen} — {issue.lastSeen}
                      </p>
                    </div>
                    {issue.stillOpen && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full shrink-0" style={{ background: colors.redDim, color: colors.red }}>
                        Open
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Cross-Property Patterns */}
          <Card className="mb-6">
            <h3 className="text-sm font-semibold mb-4" style={{ color: colors.textSecondary }}>
              Cross-Property Patterns
            </h3>
            {crossPatterns.length === 0 ? (
              <p className="text-xs" style={{ color: colors.textMuted }}>No systemic patterns detected.</p>
            ) : (
              <div className="space-y-2">
                {crossPatterns.map((pattern, i) => (
                  <div key={i} className="p-3 rounded-lg" style={{ background: colors.inputBg }}>
                    <p className="text-sm" style={{ color: colors.textPrimary }}>{pattern.description}</p>
                    <p className="text-xs mt-1" style={{ color: colors.textMuted }}>
                      {pattern.unitCount} units affected &middot; {pattern.totalOccurrences} total occurrences
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Knowledge Coverage */}
          {knowledgeStats && (
            <Card className="mb-6">
              <h3 className="text-sm font-semibold mb-4" style={{ color: colors.textSecondary }}>
                <BookOpen size={14} className="inline mr-1" /> Knowledge Coverage
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                <div>
                  <p className="text-2xl font-bold" style={{ color: colors.textPrimary }}>{knowledgeStats.totalNotes}</p>
                  <p className="text-xs" style={{ color: colors.textMuted }}>Total Notes</p>
                </div>
                <div>
                  <p className="text-2xl font-bold" style={{ color: colors.textPrimary }}>
                    {knowledgeStats.unitsCovered}/{knowledgeStats.totalUnits}
                  </p>
                  <p className="text-xs" style={{ color: colors.textMuted }}>Units Covered</p>
                </div>
              </div>
              {Object.keys(knowledgeStats.categoryCounts).length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {Object.entries(knowledgeStats.categoryCounts).map(([cat, count]) => (
                    <span key={cat} className="text-xs px-2 py-1 rounded-full" style={{ background: colors.inputBg, color: colors.textSecondary }}>
                      {KNOWLEDGE_CATEGORIES[cat]?.label || cat}: {count}
                    </span>
                  ))}
                </div>
              )}
              {knowledgeStats.topContributors.length > 0 && (
                <div>
                  <p className="text-xs mb-2" style={{ color: colors.textMuted }}>Top Contributors</p>
                  <div className="flex flex-wrap gap-2">
                    {knowledgeStats.topContributors.map(c => (
                      <span key={c.name} className="text-xs px-2 py-1 rounded-full" style={{ background: colors.yellowDim, color: colors.yellow }}>
                        {c.name} ({c.count})
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          )}
        </>
      )}

      {/* Seasonal Trends */}
      <Card className="mb-6">
        <h3 className="text-sm font-semibold mb-4" style={{ color: colors.textSecondary }}>
          Seasonal Trends {selectedPropertyId ? `— ${selectedProperty?.name || ''}` : '(All Properties)'}
        </h3>
        <SeasonalChart data={seasonalData} />
      </Card>

      {/* Tech Reliability */}
      <Card className="mb-6">
        <h3 className="text-sm font-semibold mb-4" style={{ color: colors.textSecondary }}>
          <Users size={14} className="inline mr-1" /> Tech Reliability
        </h3>
        {techReliability.length === 0 ? (
          <EmptyState title="No tech data available" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <th className="text-left text-xs py-2 pr-4" style={{ color: colors.textMuted }}>Tech</th>
                  <th className="text-left text-xs py-2 pr-4" style={{ color: colors.textMuted }}>Confidence</th>
                  <th className="text-right text-xs py-2 pr-4" style={{ color: colors.textMuted }}>Score</th>
                  <th className="text-left text-xs py-2 pr-4" style={{ color: colors.textMuted }}>Level</th>
                  <th className="text-right text-xs py-2" style={{ color: colors.textMuted }}>WOs</th>
                </tr>
              </thead>
              <tbody>
                {techReliability.map(tech => (
                  <tr key={tech.name} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <td className="text-sm py-2 pr-4" style={{ color: colors.textPrimary }}>{tech.name}</td>
                    <td className="py-2 pr-4"><ConfidenceBadge score={tech.score} size="md" /></td>
                    <td className="text-sm text-right py-2 pr-4" style={{ color: colors.textPrimary }}>{tech.score}</td>
                    <td className="text-xs py-2 pr-4" style={{ color: colors.textSecondary }}>{tech.level}</td>
                    <td className="text-sm text-right py-2" style={{ color: colors.textMuted }}>{tech.totalWOs}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
